import { SendOutlined } from '@ant-design/icons';
import { Button, Dropdown, Input } from 'antd';
import { FileText, Images, LayoutTemplate, Plus } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { sentGroupMessage, sentMessage } from '../../redux/actions/message';
import { getFileUrl } from '../../redux/actions/upload';
import { getNewFileName } from '../../utils';
import { errorToast } from '../../utils/toast';
import FileWithCaption from '../FileWithCaption';
import Template from '../Template';
import { messageType } from '../../constants';
import { decodeId } from '../../utils/secureId';
import { sender_number } from '../config';
import { store } from '../../redux/store';
import { appendMessage, removeTempMessage } from '../../redux/slices/messageSlice';

const MAX_FILE_SIZE_MB = 25;
const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;

const ChatInput = ({ disabled, isBlocked = false, isGroup = false, members = [] }) => {
    const { userId: encUserId, groupId: group_id } = useParams();
    const userId = encUserId && decodeId(encUserId);
    const groupId = group_id && decodeId(group_id);
    const [message, setMessage] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [showTemplate, setShowTemplate] = useState(false);
    const [showFileModal, setShowFileModal] = useState(false);
    const [isPageDragOver, setIsPageDragOver] = useState(false);
    const [previewFile, setPreviewFile] = useState(null);
    const documentRef = useRef(null);
    const mediaRef = useRef(null);

    const handleFileChange = async (event) => {
        const selectedFiles = Array.from(event.target.files);
        if (!selectedFiles.length) return;

        processFiles(selectedFiles);
        event.target.value = '';
    };

    const handlePaste = (event) => {
        const items = event.clipboardData?.items || [];
        const files = [];
        for (const item of items) {
            if (item.type.startsWith('image/')) {
                const file = item.getAsFile();
                if (file) files.push(file);
            }
        }
        if (files.length > 0) {
            processFiles(files);
            event.preventDefault();
        }
    };

    const processFiles = async (selectedFiles) => {

        // Define allowed types
        const allowedImageTypes = ['image/'];
        const allowedVideoTypes = ['video/'];
        const allowedDocumentTypes = [
            'application/pdf',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'text/plain',
            'application/vnd.ms-excel',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        ];

        const newFiles = [];

        for (const file of selectedFiles) {
            if (file.size > MAX_FILE_SIZE) {
                errorToast(`File "${file.name}" exceeds the ${MAX_FILE_SIZE_MB}MB size limit.`);
                continue;
            }

            let type = messageType.TEXT;
            if (allowedImageTypes.some((prefix) => file.type.startsWith(prefix))) {
                type = messageType.IMAGE;
            } else if (allowedVideoTypes.some((prefix) => file.type.startsWith(prefix))) {
                type = messageType.VIDEO;
            } else if (allowedDocumentTypes.includes(file.type)) {
                type = messageType.DOCUMENT;
            } else {
                errorToast(`File "${file.name}" has invalid type for upload.`);
                continue;
            }

            newFiles.push({
                file,
                type,
                display_name: getNewFileName(file.name),
            });
        }

        if (!newFiles.length) {
            return;
        }

        if (newFiles.length > 0) {
            const fileData = newFiles[0];
            setPreviewFile(fileData);
            setShowFileModal(true);
        }
    };

    const handleMenuClick = (key) => {
        if (key === 'documents') documentRef.current?.click();
        if (key === 'photos/videos') mediaRef.current?.click();
    };

    const handleSentMessage = async (type = 1, files = []) => {
        if ((!message && !files.length) || isSending) return;
        setIsSending(true);
        setMessage('');

        try {
            // 1. Send text message (type 1)
            if (type === messageType.TEXT && message) {
                const body = {
                    contact_id: userId,
                    type,
                    text: message,
                };

                const tempMessage = {
                    ...body,
                    sending: true,
                    id: `temp-${Date.now()}`,
                    message,
                    group_id: isGroup ? groupId : null,
                    attachment: null,
                    from_number: sender_number,
                    createdAt: new Date().toISOString(),
                }
                store.dispatch(appendMessage(tempMessage));

                isGroup
                    ? await sentGroupMessage(groupId, body)
                    : await sentMessage(body);
            }

            // 2. Send files (use passed `type` or infer from MIME type)
            for (let f of files) {
                const fileLink = getFileUrl('chats', userId, f.display_name);
                const body = {
                    contact_id: userId,
                    type: type,
                    text: null,
                    attachment: {
                        name: f.display_name,
                        type: f.file.type,
                        link: fileLink,
                    },
                };
                isGroup
                    ? await sentGroupMessage(groupId, body)
                    : await sentMessage(body);
            }
        } catch (error) {
            console.error('Error sending message:', error);
        } finally {
            setIsSending(false);
            store.dispatch(removeTempMessage());
        }
    };

    const handleKeyDown = (e) => {
        if (isSending) return;
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSentMessage();
        }
    };

    const items = [
        {
            key: 'documents',
            label: 'Documents',
            icon: <FileText size={18} color="#7f66ff" />,
            onClick: () => handleMenuClick('documents'),
            disabled,
        },
        {
            key: 'photos/videos',
            label: 'Photos & Videos',
            icon: <Images size={18} color="#007bfc" />,
            onClick: () => handleMenuClick('photos/videos'),
            disabled,
        },
        {
            key: 'templates',
            label: 'Template',
            icon: <LayoutTemplate size={18} color="#ff2e74" />,
            onClick: () => setShowTemplate(true),
        },
    ];

    useEffect(() => {
        if (disabled || isBlocked) return;

        const handleDragOver = (e) => {
            e.preventDefault();
            if (!isPageDragOver) setIsPageDragOver(true);
        };

        const handleDrop = (e) => {
            e.preventDefault();
            setIsPageDragOver(false);

            if (!e.dataTransfer?.types?.includes("Files")) {
                return;
            }

            const files = Array.from(e.dataTransfer?.files || []);
            if (files.length > 0) {
                processFiles(files);
            }
        };

        const handleDragLeave = (e) => {
            e.preventDefault();
            if (e.clientX === 0 && e.clientY === 0) {
                setIsPageDragOver(false);
            }
        };

        window.addEventListener("dragover", handleDragOver);
        window.addEventListener("drop", handleDrop);
        window.addEventListener("dragleave", handleDragLeave);

        return () => {
            window.removeEventListener("dragover", handleDragOver);
            window.removeEventListener("drop", handleDrop);
            window.removeEventListener("dragleave", handleDragLeave);
        };
    }, [isPageDragOver, disabled, isBlocked]);

    return (
        <>
            {!disabled && !isBlocked && isPageDragOver && (
                <div className="drag-overlay blur-bg">
                    <div className="drag-message">
                        <h3 className='mb-1'>Drop files here</h3>
                        <p>Drag and drop your files to upload instantly</p>
                    </div>
                </div>
            )}

            <div className="Chat__Input" onPaste={handlePaste}>
                <div className="input-container">
                    <Dropdown
                        menu={{
                            items,
                        }}
                        trigger={['click']}
                    >
                        <Button
                            icon={<Plus strokeWidth={3} />}
                            shape="circle"
                            size="large"
                            className="icon-btn"
                            disabled={isBlocked}
                        />
                    </Dropdown>

                    <div style={{ display: 'none' }}>
                        <input
                            type="file"
                            accept=".pdf,.doc,.docx,.txt"
                            ref={documentRef}
                            onChange={handleFileChange}
                            disabled={disabled}
                        />
                        <input
                            type="file"
                            accept="image/*,video/*"
                            ref={mediaRef}
                            onChange={handleFileChange}
                            disabled={disabled}
                        />
                    </div>
                    <Input.TextArea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Type a message..."
                        size="large"
                        autoSize={{ minRows: 1, maxRows: 4 }}
                        onKeyDown={handleKeyDown}
                        disabled={disabled || isBlocked}
                    />
                    <Button
                        icon={<SendOutlined />}
                        type="primary"
                        shape="circle"
                        size="large"
                        color="danger"
                        onClick={() => handleSentMessage()}
                        disabled={!message || isSending}
                    />
                </div>
            </div>
            {showTemplate && <Template open={showTemplate} onCancel={() => setShowTemplate(false)} isGroup={isGroup} />}
            {previewFile && (
                <FileWithCaption
                    open={showFileModal}
                    onCancel={() => {
                        setShowFileModal(false);
                        setPreviewFile(null);
                    }}
                    userId={userId}
                    fileData={previewFile}
                    isGroup={isGroup}
                    groupId={groupId}
                    members={members}
                />
            )}
        </>
    );
};

export default ChatInput;
